import * as Sentry from '@sentry/react';
import { AnimatePresence, m } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { Trans } from 'react-i18next';

// ----------------------------------------------------------------------

type Props = {
    open: boolean;
    onClose: () => void;
};

export default function UserFeedbackDialog({ open, onClose }: Props) {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslate();
    const authContext = useAuthContext();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isClosing, setIsClosing] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = useCallback(() => {
        if (!isMobile) {
            setIsClosing(true);
            // allow time for exit animation to complete
            setTimeout(() => {
                onClose();
                setIsClosing(false);
                setFeedback('');
            }, 400); // match exit animation duration
        } else {
            // for mobile, close immediately
            onClose();
            setFeedback(''); // reset feedback on close
        }
    }, [isMobile, onClose]);

    const handleSubmit = useCallback(async () => {
        if (!feedback.trim()) {
            enqueueSnackbar('Please provide your feedback before submitting.', { variant: 'warning' });
            return;
        }

        if (!authContext) {
            enqueueSnackbar(t('components.user_feedback_dialog.auth_error'), { variant: 'error' });
            return;
        }

        setIsSubmitting(true);

        try {
            // submit feedback to Sentry
            Sentry.captureFeedback({
                name: `${authContext.user.first_name}  ${authContext.user.last_name}`,
                email: authContext.user.email,
                url: window.location.href,

                message: feedback,
            }, {
                attachments: [],
                includeReplay: true, // Include replay if available
                data: {
                    auth_context: authContext
                }
            });

            enqueueSnackbar(t('components.user_feedback_dialog.success_message'), { variant: 'success' });
            handleClose();
        } catch (error) {
            console.error('Error submitting feedback:', error);
            enqueueSnackbar(t('components.user_feedback_dialog.error_message'), { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    }, [feedback, enqueueSnackbar, handleClose, authContext, t]);

    const renderFeedbackForm = () => (
        <>
            <Box sx={{ mb: 2 }}>
                <Trans>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('components.user_feedback_dialog.description')}
                    </Typography>
                </Trans>

                <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Share your feedback here..."
                    variant="outlined"
                    sx={{ mb: 2 }}
                />
            </Box>
        </>
    );

    // Mobile view - modal dialog
    if (isMobile) {
        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open}
                sx={{ zIndex: theme.zIndex.modal + 1 }}
                onClose={handleClose}
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            {t('components.user_feedback_dialog.title')}
                        </Typography>

                        <IconButton
                            edge="end"
                            onClick={handleClose}
                        >
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    {renderFeedbackForm()}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        {t('common.cancel')}
                    </Button>

                    <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={isSubmitting}
                        onClick={handleSubmit}
                        disabled={!feedback.trim()}
                    >
                        {t('components.user_feedback_dialog.submit')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        );
    }

    // Desktop view - Floating card
    return (
        <AnimatePresence mode="wait">
            {(open || isClosing) && (
                <m.div
                    key="user-feedback-dialog"
                    initial={{ y: 100, opacity: 0 }}
                    animate={isClosing ? { y: 100, opacity: 0 } : { y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{
                        duration: 0.3,
                        ease: 'easeInOut',
                        exit: { duration: 0.4 } // Slightly slower exit animation
                    }}
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        maxWidth: 400,
                        width: '100%',
                        zIndex: theme.zIndex.modal + 1,
                    }}
                >
                    <Card sx={{ boxShadow: theme.customShadows.z20 }}>
                        <Box sx={{ position: 'relative', p: 3 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="h6">
                                    {t('components.user_feedback_dialog.title')}
                                </Typography>

                                <IconButton
                                    size="small"
                                    sx={{ p: 0 }}
                                    onClick={handleClose}
                                >
                                    <Iconify icon="eva:close-fill" />
                                </IconButton>
                            </Stack>

                            <Divider sx={{ mb: 2 }} />

                            {renderFeedbackForm()}

                            <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleClose}
                                    size="small"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {t('common.cancel')}
                                </Button>

                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={isSubmitting}
                                    onClick={handleSubmit}
                                    disabled={!feedback.trim()}
                                    size="small"
                                    startIcon={<Iconify icon="mdi:send" />}
                                >
                                    {t('components.user_feedback_dialog.submit')}
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Card>
                </m.div>
            )}
        </AnimatePresence>
    );
}